import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AuthService, OCC_CART_ID_CURRENT } from '@spartacus/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, groupBy, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType } from '../../../../../core/global-message/models/global-message.model';
import { GlobalMessageActions } from '../../../../../core/global-message/store/actions';
import { isErrorGloballyHandled } from '../../../../../core/global-message/utils/message-util';
import { OrderProcessState } from '../../../../../core/model';
import { Cart, CartType } from '../../../../../core/model/cart.model';
import { CurrenciesActionTypes } from '../../../../../core/site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../../../core/site-context/store/actions/languages.action';
import { normalizeHttpError } from '../../../../../core/util';
import { withdrawOn } from '../../../../../core/util/rxjs/withdraw-on';
import { AppActions } from '../../../../../core/util/store/actions';
import { isCartNotFoundError } from '../../../../../shared';
import { StateWithCheckout } from '../../../../checkout/base/store/checkout-state';
import { CheckoutSelectors } from '../../../../checkout/base/store/selectors';
import { CartConnector } from '../../connector/cart.connector';
import { CartEntryActionTypes } from '../actions/cart-entry.action';
import { CartActionTypes } from '../actions/cart.action';
import { CartActions } from '../actions/index';
import { StateWithCart } from '../cart-state';
import { getCartHasPendingProcessesFactory } from '../selectors/cart.selector';

@Injectable()
export class CartEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );

  loadCart$: Observable<
    CartActions.LoadCartFail | CartActions.LoadCartSuccess | CartActions.RemoveCart | CartActions.RemoveTinyCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActionTypes.LoadCart),
      map((action: CartActions.LoadCart) => action.payload),
      withLatestFrom(this.authService.isUserLoggedIn()),
      filter(([_payload, loggedIn]) => Boolean(loggedIn)),
      groupBy(([payload, _loggedIn]) => payload.cartId),
      mergeMap((group$) =>
        group$.pipe(
          switchMap(([payload]) => {
            return combineLatest([
              this.store.pipe(select(getCartHasPendingProcessesFactory(payload.cartId))),
              this.store.pipe(select(CheckoutSelectors.getOrderProcessStatusFactory(payload.cartType ?? CartType.stock))),
            ]).pipe(
              filter(
                ([hasPendingProcesses, orderProcessStatus]) =>
                  !hasPendingProcesses && orderProcessStatus?.orderProcessState !== OrderProcessState.RUNNING
              ),
              map(() => payload),
              take(1)
            );
          }),
          switchMap((payload) => {
            return this.connector
              .load(payload.userId, payload.cartId === OCC_CART_ID_CURRENT ? payload.cartType : payload.cartId)
              .pipe(
                mergeMap((cart: Cart) => {
                  let actions = [];
                  if (cart) {
                    actions.push(
                      new CartActions.LoadCartSuccess({
                        ...payload,
                        cart,
                        cartId: cart.code,
                      })
                    );
                    if (payload.cartId === OCC_CART_ID_CURRENT) {
                      // Removing cart from entity object under `current` key as it is no longer needed.
                      // Current cart is loaded under its code entity.
                      actions.push(
                        new CartActions.RemoveCart({
                          cartType: payload.cartType,
                          cartId: OCC_CART_ID_CURRENT,
                        })
                      );
                    }
                  } else {
                    actions = [
                      new CartActions.LoadCartFail({
                        ...payload,
                        error: {},
                      }),
                    ];
                  }
                  return actions;
                }),
                catchError((error) => {
                  if (error?.error?.errors) {
                    const cartNotFoundErrors = error.error.errors.filter(
                      (err) => isCartNotFoundError(err) || err.reason === 'UnknownResourceError'
                    );
                    if (cartNotFoundErrors.length > 0) {
                      // Remove cart as it doesn't exist on backend.
                      return from([
                        new CartActions.RemoveCart({ cartType: payload.cartType, cartId: payload.cartId }),
                        new CartActions.RemoveTinyCart({ cartType: payload.cartType, cartId: payload.cartId }),
                      ]);
                    }
                  }
                  return of(
                    new CartActions.LoadCartFail({
                      ...payload,
                      error: normalizeHttpError(error),
                    })
                  );
                })
              );
          })
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  refreshWithoutProcesses$: Observable<CartActions.LoadCart | CartActions.LoadTinyCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ...[].concat(
          CartEntryActionTypes.AddCartEntrySuccess,
          CartEntryActionTypes.AddA4SampleCartEntrySuccess,
          CartEntryActionTypes.QuickAddCartEntrySuccess,
          CartEntryActionTypes.AddCartEntriesSuccess,
          CartEntryActionTypes.RemoveCartEntrySuccess,
          CartEntryActionTypes.RemoveCartEntriesSuccess,
          CartEntryActionTypes.RemoveAllCartEntriesSuccess,
          CartEntryActionTypes.UpdateCartEntrySuccess,
          CartEntryActionTypes.ReplaceCartEntrySuccess
        )
      ),
      mergeMap((action) => {
        const payload = action.payload;
        const actions = [];
        actions.push(
          new CartActions.LoadCart({
            userId: payload.userId,
            cartType: payload.cartType,
            cartId: payload.cartId,
          })
        );
        const shouldUpdateTinyCart = ![
          CartEntryActionTypes.UpdateCartEntrySuccess,
          CartEntryActionTypes.ReplaceCartEntrySuccess,
        ].includes(action.type);
        if (shouldUpdateTinyCart) {
          actions.push(
            new CartActions.LoadTinyCart({
              userId: payload.userId,
              cartId: payload.cartId,
            })
          );
        }
        if (payload?.failedArticlesData?.length) {
          actions.push(
            new GlobalMessageActions.AddMessage({
              text: {
                key: 'cart.addCartEntriesFailure_message',
                details: payload.failedArticlesData?.map((detail: any) => `${detail.articleNumber} : ${detail.errorMessage}`),
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            })
          );
        }
        return actions;
      })
    )
  );

  handleFailureForLoadCart$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActionTypes.LoadCartFail),
      map((action: CartActions.LoadCartFail) => {
        if (!isErrorGloballyHandled(action.payload?.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'cart.loadCartFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
            timeout: 10000,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForLoadCart$: Observable<CartActions.ClearPrePrintedLabels | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActionTypes.LoadCartSuccess),
      map((action: CartActions.LoadCartSuccess) => {
        if (action.payload?.cartType === CartType.stock) {
          return new CartActions.ClearPrePrintedLabels();
        }
        return new AppActions.NoOpAction();
      })
    )
  );

  resetCartDetailsOnSiteContextChange$: Observable<CartActions.ResetCartDetails> = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguagesActionTypes.LanguageChange, CurrenciesActionTypes.CurrencyChange),
      mergeMap(() => {
        return [new CartActions.ResetCartDetails()];
      })
    )
  );

  constructor(
    private actions$: Actions,
    private connector: CartConnector,
    private store: Store<StateWithCart & StateWithCheckout>,
    private authService: AuthService
  ) {}
}
